import {DateTime, DefaultHttpClient, HttpRequest, HttpRequestResult, nanoid} from "@2d-front-monorepo/core";
import {InstallationId} from "../../nominalTypes/InstallationId";
import IPermitsAuthentication from "../IPermitsAuthentication";
import IPermitsCustomActions from "../IPermitsCustomActions";
import IPermitsDrivers from "../IPermitsDrivers";
import IPermitsFiles from "../IPermitsFiles";
import IPermitsGlobal from "../IPermitsGlobal.js";
import IPermitsHolder from "../IPermitsHolder";
import IPermitsHttpClient from "../IPermitsHttpClient";
import IPermitsPayments from "../IPermitsPayments";
import IPermitsPermits from "../IPermitsPermits";
import IPermitsReservations from "../IPermitsReservations";
import IPermitsResidencies from "../IPermitsResidencies";
import IPermitsTariffs from "../IPermitsTariffs";
import IPermitsVehicles from "../IPermitsVehicles";
import {PermitsAuthentication} from "./PermitsAuthentication";
import {PermitsCustomActions} from "./PermitsCustomActions";
import {PermitsDrivers} from "./PermitsDrivers";
import {PermitsFiles} from "./PermitsFiles";
import {PermitsGlobal} from "./PermitsGlobal";
import {PermitsHolder} from "./PermitsHolder";
import {PermitsPayments} from "./PermitsPayments";
import {PermitsPermits} from "./PermitsPermits";
import {PermitsReservations} from "./PermitsReservations";
import {PermitsResidencies} from "./PermitsResidencies";
import {PermitsTariffs} from "./PermitsTariffs";
import {PermitsVehicles} from "./PermitsVehicles";

class PermitsFrontClient extends DefaultHttpClient implements IPermitsHttpClient {
	private readonly _getClientNow: () => DateTime | null;
	private readonly _globalErrorHandler: (error: Error) => void;
	private readonly _installationId: InstallationId | undefined;
	private readonly _sessionId: string;
	_authentication: IPermitsAuthentication | null;
	_customActions: IPermitsCustomActions | null;
	_drivers: IPermitsDrivers | null;
	_files: IPermitsFiles | null;
	_global: IPermitsGlobal | null;
	_holder: IPermitsHolder | null;
	_payments: IPermitsPayments | null;
	_permits: IPermitsPermits | null;
	_reservations: IPermitsReservations | null;
	_residencies: IPermitsResidencies | null;
	_tariffs: IPermitsTariffs | null;
	_vehicles: IPermitsVehicles | null;

	constructor(baseUrl: string, installationId: InstallationId | undefined, globalErrorHandler: (error: Error) => void, getClientNow: () => DateTime | null) {
		super(baseUrl, {
			credentials: "include"
		});

		this._getClientNow = getClientNow;
		this._globalErrorHandler = globalErrorHandler;
		this._global = null;
		this._authentication = null;
		this._files = null;
		this._holder = null;
		this._permits = null;
		this._reservations = null;
		this._drivers = null;
		this._residencies = null;
		this._vehicles = null;
		this._payments = null;
		this._tariffs = null;
		this._customActions = null;
		this._sessionId = nanoid();
		this._installationId = installationId;
		this.addPipeline(this._addHeaders.bind(this));
		this.addPipeline(this._errorHandler.bind(this));
	}

	get global(): IPermitsGlobal {
		return this._global ??= new PermitsGlobal(this);
	}

	get authentication(): IPermitsAuthentication {
		return this._authentication ??= new PermitsAuthentication(this);
	}

	get files(): IPermitsFiles {
		return this._files ??= new PermitsFiles(this);
	}

	get holder(): IPermitsHolder {
		return this._holder ??= new PermitsHolder(this);
	}

	get permits(): IPermitsPermits {
		return this._permits ??= new PermitsPermits(this);
	}

	get reservations(): IPermitsReservations {
		return this._reservations ??= new PermitsReservations(this);
	}

	get drivers(): IPermitsDrivers {
		return this._drivers ??= new PermitsDrivers(this);
	}

	get residencies(): IPermitsResidencies {
		return this._residencies ??= new PermitsResidencies(this);
	}

	get vehicles(): IPermitsVehicles {
		return this._vehicles ??= new PermitsVehicles(this);
	}

	get payments(): IPermitsPayments {
		return this._payments ??= new PermitsPayments(this);
	}

	get tariffs(): IPermitsTariffs {
		return this._tariffs ??= new PermitsTariffs(this);
	}

	get customActions(): IPermitsCustomActions {
		return this._customActions ??= new PermitsCustomActions(this);
	}

	private _addHeaders(request: HttpRequest, next: (() => Promise<HttpRequestResult>)): Promise<HttpRequestResult> {
		request.headers["X-Session-Id"] = this._sessionId;
		if (this._installationId != null) request.headers["X-User-Installation"] = this._installationId;
		if (request.method === "POST" || request.method === "PUT" || request.method === "PATCH" || request.method === "DELETE") {
			// Fresh key per logical call, set once and reused by the client's internal network
			// retries — so an ambiguous retry can't double-create, while a deliberate identical
			// resubmit (or a FormData upload, which the old body-hash skipped) gets its own key.
			request.headers["Idempotency-Key"] = nanoid();
			const now = this._getClientNow();
			if (now != null) request.headers["X-Client-Now"] = now.toString();
		}
		return next();
	}

	private async _errorHandler(request: HttpRequest, next: (() => Promise<HttpRequestResult>)): Promise<HttpRequestResult> {
		const r = await next();
		if (r.error != null) {
			this._globalErrorHandler(r.error);
		}
		return r;
	}
}

export {PermitsFrontClient};
